/**
 * Device-local "has seen the first-run intro" flag for each Warmup game mode.
 *
 * Mirrors `src/tutorial/helpers/walkthroughSeenStorage.helper.ts`: web reads/writes `localStorage`,
 * native uses AsyncStorage, and every operation swallows errors (fail open → show the intro) so storage
 * never blocks play. Intentionally NOT keyed by userId — warmup is playable anonymously and seen-state
 * is per device (see specs/warmup-game-intros/spec.md AC-7).
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';
import {
  WARMUP_INTRO_MODES,
  WARMUP_INTRO_STORAGE_PREFIX,
  type WarmupIntroMode,
} from '../constants/warmupIntros.constants';

const storageKey = (mode: WarmupIntroMode) => `${WARMUP_INTRO_STORAGE_PREFIX}${mode}`;

async function readFlag(key: string): Promise<boolean> {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return localStorage.getItem(key) === 'true';
    }
    const value = await AsyncStorage.getItem(key);
    return value === 'true';
  } catch (error) {
    if (__DEV__) logger.warn('warmupIntroSeenStorage: read failed', { key, error });
    return false;
  }
}

async function writeFlag(key: string): Promise<void> {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem(key, 'true');
      return;
    }
    await AsyncStorage.setItem(key, 'true');
  } catch (error) {
    if (__DEV__) logger.warn('warmupIntroSeenStorage: write failed', { key, error });
  }
}

/** True once the player has dismissed this mode's intro on this device. */
export async function hasSeenWarmupIntro(mode: WarmupIntroMode): Promise<boolean> {
  return readFlag(storageKey(mode));
}

/** Persist that this mode's intro has been seen (first-time-only). */
export async function markWarmupIntroSeen(mode: WarmupIntroMode): Promise<void> {
  return writeFlag(storageKey(mode));
}

/** Read every mode's seen flag at once, so the open decision is instant after preload. */
export async function loadSeenWarmupIntros(): Promise<Record<WarmupIntroMode, boolean>> {
  const entries = await Promise.all(
    WARMUP_INTRO_MODES.map(async (mode) => [mode, await hasSeenWarmupIntro(mode)] as const),
  );
  return Object.fromEntries(entries) as Record<WarmupIntroMode, boolean>;
}
